<script setup lang="ts">
import type {PropType} from "vue";

const props = defineProps({
  close: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  priceChange24h: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  priceChange7d: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  vwap: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  baseVolume: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  quoteVolume: {
    type: Number as PropType<number | undefined>,
    required: false,
    default: undefined
  },
  quote: {type: String, required: true},
  crypto: {type: String, required: true},
  quoteName: {type: String, required: true},
  cryptoName: {type: String, required: true},
});

const allowableNums = [
  0.0005, 0.001, 0.005, 0.01, 0.05, 0.1, 0.5, 1, 5, 10, 50, 100, 500, 1000, 5000,
  10000, 50000, 100000, 500000, 1000000, 5000000, 10000000, 50000000, 100000000, 500000000,
  1000000000, 5000000000, 10000000000, 50000000000, 100000000000, 500000000000, 1000000000000,
  5000000000000, 10000000000000, 50000000000000, 100000000000000, 500000000000000, 1000000000000000,
];

function firstAmountToValueMoreThanHundreds(amount: number | undefined): number[] {
  if (!amount || amount <= 0) return [];

  // Calculate the minimum multiplier needed to exceed 1000
  const minMultiplier = Math.ceil(1000 / amount);

  // Find the index of the first number in allowableNums that's >= minMultiplier
  const closestNumberToMultiplierIndex = allowableNums.findIndex(num => num >= minMultiplier);

  // If no suitable multiplier found in allowableNums, return empty array
  if (closestNumberToMultiplierIndex === -1) return [];

  // Return a slice of allowableNums around the found index
  const startIndex = Math.max(0, closestNumberToMultiplierIndex / 2);
  const endIndex = Math.min(allowableNums.length, startIndex + 10);

  return allowableNums.slice(startIndex, endIndex);
}

const multiplierNumbers = firstAmountToValueMoreThanHundreds(props.close);

function formatNumber(num: number): string {
  return num.toLocaleString('en-US', {maximumFractionDigits: 8});
}

const conversionTables = [
  {
    title: `${props.crypto} to ${props.quote} Price Table`,
    rows: multiplierNumbers.map(num => ({
      from: {amount: formatNumber(num), currency: props.crypto},
      to: {amount: formatNumber(num * (props.close ?? 0)), currency: props.quote}
    }))
  },
  {
    title: `${props.quote} to ${props.crypto} Price Table`,
    rows: multiplierNumbers.map(num => ({
      from: {amount: formatNumber(num), currency: props.quote},
      to: {amount: formatNumber(num / (props.close ?? 1)), currency: props.crypto}
    }))
  }
];
</script>
<template>
  <div class="converters-wrapper w-full">
    <div
        v-for="(table, tableIndex) in conversionTables"
        :key="tableIndex"
        class="converter-container p-6 border-2 border-brand-stroke/10 rounded-4 bg-brand-background text-white"
    >
      <h2 class="text-4xl font-bold mb-6">{{ table.title }}</h2>
      <div class="flex justify-between text-brand-secondary mb-4">
        <span>{{ $t('asset') }}</span>
        <span>{{ $t('today_at') }} 00:01</span>
      </div>
      <div class="conversion-rows">
        <div v-for="(row, index) in table.rows" :key="index" class="conversion-row">
          <span class="text-xl font-medium">{{ row.from.amount }} {{ row.from.currency }}</span>
          <span class="text-xl font-medium">{{ row.to.amount }} {{ row.to.currency }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.converters-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2rem; /* Adds a 2rem gap between converter containers */
}

.converter-container {
}

.conversion-rows {
  display: flex;
  flex-direction: column;
}

.conversion-row {
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.conversion-row:last-child {
  border-bottom: none;
}
</style>